import './mailCodePinVerifyPage.scss';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import Snackbar from '@mui/material/Snackbar';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { get, isEmpty, isNaN, uniqueId } from 'lodash';
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import logo from '../../../assets/logo-default.svg';
import vehicleSUVTopView from '../../../assets/vehicle-suv-topview.png';
import { useCountdownTimer } from '../../../hooks/useCountdownTimer/useCountdownTimer';
import useApiStore from '../../../store/apiStore/useApiStore';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import { HydrateSource } from '../../../store/applyStore/enums/HydrateSource';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useMailCodeStore from '../../../store/mailCodeStore/useMailCodeStore';
import useParameterStore from '../../../store/parameterStore/useParameterStore';
import { DeliverPinLocation } from '../../../store/pinStore/enums/DeliverPinLocation';
import usePinStore from '../../../store/pinStore/usePinStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { inputEventHandler } from '../../../utils/component/inputEventHandler';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { t } = useTranslation();
  const { isLoading } = useApiStore();
  const { applicationType, leadChannelCode } = useParameterStore();
  const { hydrate } = useApplyStore();
  const { updateCurrentResidencePhysicalAddress } = useApplicantStore();
  const { deliverPin, deliverPinRetrySuccess, deliverPinFailedMessage } =
    usePinStore();
  const {
    computed: {
      applicantPhysicalAddress,
      applyRoute,
      hasValidApplicantPhysicalAddress,
      isPrimaryPhoneNumberEmpty,
      vehiclePayoffAmount,
    },
    firstName,
    lastName,
    primaryPhoneNumber,
    primaryEmailAddress,
    resolveMailCodeState,
  } = useMailCodeStore();
  const {
    clearPinPurpose,
    setUserFirstName,
    setUserLastName,
    userAuthenticate,
    userCreate,
    userUpdate,
    userCreateFailedMessage,
    userAuthenticateFailedMessage,
    computed: { shouldCreateUser, isPinPurposeEmpty },
    pinPurpose,
    getDeliverPinParams,
  } = useUserStore();
  const { register, handleSubmit, reset, getValues, setValue } = useForm();

  const amountOfInputs = 6;
  const InputsList = [...Array(amountOfInputs).keys()];
  const inputRef = useRef<any>([]);
  const [focusedInputIndex, setFocusedInputIndex] = useState(0);

  const resendTimerDuration = 15; // in seconds
  const [seconds, setSecondsLeft] = useState(0);
  const { startTimer, stopTimer } = useCountdownTimer(
    resendTimerDuration,
    setSecondsLeft,
  );

  useEffect(() => {
    if (isPrimaryPhoneNumberEmpty || isPinPurposeEmpty) {
      navigate('/mail');
    }

    startTimer();

    return (): void => {
      stopTimer();
    };
  }, [primaryPhoneNumber]);

  useEffect(() => {
    if (userCreateFailedMessage || userAuthenticateFailedMessage) {
      reset();
    }
  }, [userCreateFailedMessage, userAuthenticateFailedMessage]);

  const buttonText = useMemo(() => {
    if (seconds > 0) {
      return t('MailCodePinVerifyPage.resendCodeDelay', {
        delay: seconds,
      });
    }
    return t('MailCodePinVerifyPage.resendCode');
  }, [t, seconds]);

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const pin = Object.values(values).join('');

    if (!pin) {
      return;
    }

    if (shouldCreateUser) {
      await userCreate(
        firstName,
        lastName,
        primaryEmailAddress,
        primaryPhoneNumber,
        pin,
      );
    } else {
      await userAuthenticate(
        primaryPhoneNumber,
        pin,
        leadChannelCode,
        pinPurpose,
      );

      setUserFirstName(firstName);
      setUserLastName(lastName);

      await userUpdate();
    }

    if (hasValidApplicantPhysicalAddress) {
      await updateCurrentResidencePhysicalAddress(applicantPhysicalAddress);
    }

    const { userAuthenticateSuccess, userCreateSuccess } =
      useUserStore.getState();

    if (userCreateSuccess || userAuthenticateSuccess) {
      await hydrate({
        applicationType,
        payoffAmount: vehiclePayoffAmount,
        source: HydrateSource.MAIL_CODE,
      });

      navigateWithCallback(applyRoute).then(() => {
        resolveMailCodeState();
        clearPinPurpose();
      });
    }
  };

  const focusCurrentInput = (): void => {
    const formValues = Object.values(getValues()).join('');
    const numOfFilledInputs = formValues.length;

    const currentInput = inputRef.current[numOfFilledInputs];
    if (currentInput) {
      currentInput.focus();
    }
  };

  const determineInputFocus =
    (index: number) =>
    (event: KeyboardEvent<HTMLInputElement>): void => {
      // Detect backspace to focus the previous input
      if (event.key === 'Backspace' || event.key === 'Delete') {
        const previousInput = inputRef.current[index - 1];

        if (previousInput) {
          setValue(`verificationCode-${index - 1}`, '');

          previousInput.focus();
        }
      }

      // Detect current input value was not provided.
      // Needed to prevent focusing of next input due to
      // holding down of multiple keys at once
      const currentInput = inputRef.current[index];
      if (!currentInput.value) {
        return;
      }

      // Detect number was entered to focus the next input
      if (!isNaN(parseInt(event.key, 10))) {
        const nextInput = inputRef.current[index + 1];

        if (nextInput) {
          nextInput.focus();
        } else {
          currentInput.blur();

          // Submit form when last input value was provided
          handleSubmit(formSubmit)();
        }
      }
    };

  const splitOneTimeCode = (event: any): void => {
    const value = get(event, 'target.value');

    if (isNaN(parseInt(value, 10)) || value.length !== amountOfInputs) {
      return;
    }

    const chars: string[] = value.split('');

    chars.forEach((char, index) => {
      setValue(`verificationCode-${index}`, char);
    });

    const lastInput = inputRef.current[inputRef.current.length - 1];
    if (lastInput) {
      lastInput.focus();
    }

    handleSubmit(formSubmit)();
  };

  const capturePasteEvent = (event: any): void => {
    // get clipboard data from paste event
    const clipboardData = get(event, 'clipboardData');

    if (clipboardData) {
      const value = clipboardData.getData('Text');

      // prevent paste if non-digits were provided
      if (!/^\d+$/g.test(value)) {
        event.preventDefault();
        return;
      }

      let nextInput;

      const maxCharsProvided = value.length >= amountOfInputs;

      if (value.length > 1) {
        // handle multiple digits being pasted
        // convert value to chars array, sliced to the max amount of inputs
        const chars: string[] = value.split('').slice(0, amountOfInputs);

        // assign the char value to each input
        chars.forEach((char, index) => {
          setValue(`verificationCode-${index}`, char);
        });

        // determine the next input index
        const lastCharInputIndex = maxCharsProvided
          ? amountOfInputs - 1 // next input should be the last if max characters
          : value.length;

        nextInput = inputRef.current[lastCharInputIndex];
      } else if (value.length === 1) {
        // single digit was pasted, set value the currently focused input
        setValue(`verificationCode-${focusedInputIndex}`, value);

        // determine the next input
        nextInput = inputRef.current[focusedInputIndex + 1];
      }

      // prevent paste event from carrying over to the next input when focused
      event.preventDefault();

      // added as a fail safe but the next input to focus should always exist
      if (nextInput) {
        nextInput.focus();
      }

      // all chars were provided, submit the form
      if (maxCharsProvided) {
        handleSubmit(formSubmit)();
      }
    }
  };

  const resendCode = async (): Promise<void> => {
    if (primaryPhoneNumber) {
      startTimer();

      useUserStore.setState({
        userCreateFailedMessage: null,
        userAuthenticateFailedMessage: null,
        userAuthenticateLocked: false,
        userAuthenticateSuccess: false,
      });

      const deliverPinParams = getDeliverPinParams(
        DeliverPinLocation.RETRY,
        primaryPhoneNumber,
        primaryEmailAddress,
      );

      await deliverPin(deliverPinParams);

      inputRef.current[0].focus();
    }
  };

  return (
    <div className="mail-code-pin-verify-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          {deliverPinRetrySuccess && (
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={5000}
              open
              onClose={() => {
                usePinStore.setState({ deliverPinRetrySuccess: false });
              }}
            >
              <Alert
                data-testid={locator.pages.mailCodePinVerify.alert.success}
                icon={<CheckCircleIcon />}
                severity="success"
              >
                {t('MailCodePinVerifyPage.codeSent')}
              </Alert>
            </Snackbar>
          )}

          {!isEmpty(deliverPinFailedMessage) && (
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={5000}
              open
              onClose={() => {
                usePinStore.setState({ deliverPinFailedMessage: null });
              }}
            >
              <Alert
                data-testid={locator.pages.mailCodePinVerify.alert.error}
                severity="error"
              >
                {deliverPinFailedMessage}
              </Alert>
            </Snackbar>
          )}

          <header>
            <img alt={t('imageAlts.logo')} src={logo} />
          </header>

          <main>
            <figure>
              <img
                alt={t('imageAlts.vehicleSUVTopView')}
                src={vehicleSUVTopView}
              />
            </figure>
            <form noValidate onSubmit={handleSubmit(formSubmit)}>
              <div className="form-content">
                <h1 data-testid={locator.pages.mailCodePinVerify.form.h1}>
                  {t('MailCodePinVerifyPage.title')}
                </h1>
                <h3 data-testid={locator.pages.mailCodePinVerify.form.h3}>
                  <Trans
                    components={[<u key={uniqueId('span-')} />]}
                    i18nKey={'MailCodePinVerifyPage.subtitle'}
                    values={{
                      phoneNumber:
                        inputEventHandler.phoneNumber.formatter(
                          primaryPhoneNumber,
                        ),
                    }}
                  />
                </h3>

                <div className="inputs-container" onClick={focusCurrentInput}>
                  {InputsList.map((index) => (
                    <TextField
                      {...register(`verificationCode-${index}`)}
                      autoComplete={'one-time-code'}
                      autoFocus={index === 0}
                      disabled={isLoading}
                      error={
                        !isEmpty(userCreateFailedMessage) ||
                        !isEmpty(userAuthenticateFailedMessage)
                      }
                      inputProps={{
                        'data-testid':
                          locator.pages.mailCodePinVerify.form[
                            `pinInput${index + 1}`
                          ],
                        inputMode: 'numeric',
                      }}
                      inputRef={(element: HTMLInputElement) => {
                        inputRef.current[index] = element;
                        return element;
                      }}
                      key={index}
                      style={{
                        pointerEvents:
                          focusedInputIndex !== index ? 'none' : 'auto',
                      }}
                      onChange={splitOneTimeCode}
                      onFocus={() => {
                        setFocusedInputIndex(index);
                      }}
                      onKeyDown={inputEventHandler.verificationCode.keyDown}
                      onKeyUp={determineInputFocus(index)}
                      onPasteCapture={capturePasteEvent}
                    />
                  ))}
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  {(!isEmpty(userCreateFailedMessage) ||
                    !isEmpty(userAuthenticateFailedMessage)) && (
                    <span
                      data-testid={
                        locator.pages.mailCodePinVerify.form.pinInputError
                      }
                      style={{
                        fontSize: '12px',
                        color: red[700],
                      }}
                    >
                      {userCreateFailedMessage || userAuthenticateFailedMessage}
                    </span>
                  )}
                </div>

                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.mailCodePinVerify.form.resendPinLink
                  }
                  disabled={seconds > 0}
                  loading={isLoading}
                  type="button"
                  variant="contained"
                  fullWidth
                  onClick={resendCode}
                >
                  {buttonText}
                </LoadingButton>
              </div>
              <footer data-testid={locator.pages.mailCodePinVerify.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'MailCodePinVerifyPage';
